import _ from 'lodash'
import type {CssValue} from '../../constants'
import type {DecoratedStyleProperty, FontOverrides} from '../../../../../decorations/styleProperties/constants'
import {hasValidKeys} from '../utils/object'
import {deserializeVariable, getVarValueRegex, serializeVariable} from '../utils/var'
import {OVERRIDE_FUNCTION_NAME, deserializeOverride, serializeOverride} from '../utils/override'
import {ARGS_DELIMITER} from '../utils/function'
import {deserializeString} from '../utils/string'

const DEFAULT_VALUE = 'initial'
const FONT_VAR_PREFIX: string = 'font'
const FONT_OVERRIDES = ['fontStyle', 'fontWeight', 'fontSize', 'fontFamily']
const FONT_OVERRIDES_DELIMITER = ' '
const VALID_KEYS = new Set(['value', 'overrides'])
const OVERRIDES_DATA_KEYS = new Set(FONT_OVERRIDES)
const FONT_VALUE_TYPES = {
    THEME: 'THEME',
    DEFAULT: 'DEFAULT',
    VALUE: 'VALUE'
}

interface CssFontValue {
    fontValue?: CssValue
    fontType: string
}

const fontThemeVarRegex = getVarValueRegex(FONT_VAR_PREFIX)
const serializeFontValue = (value?: DecoratedStyleProperty['value']): CssFontValue => {
    if (!value) {
        return {fontValue: DEFAULT_VALUE, fontType: FONT_VALUE_TYPES.DEFAULT}
    }

    if (fontThemeVarRegex.test(value)) {
        return {fontValue: serializeVariable({variable: value}), fontType: FONT_VALUE_TYPES.THEME}
    }

    return {fontValue: value, fontType: FONT_VALUE_TYPES.VALUE}
}

const serializeOverrides = (overrides: FontOverrides): CssValue =>
    FONT_OVERRIDES.map((overrideKey: string) => (overrides as Record<string, any>)[overrideKey] ?? DEFAULT_VALUE).join(
        FONT_OVERRIDES_DELIMITER
    )

const deserializeFontValue = (val: CssValue): DecoratedStyleProperty['value'] => {
    if (val === DEFAULT_VALUE) {
        return DEFAULT_VALUE
    }

    const {variable, value, prefix, defaultValue} = deserializeVariable(val) ?? {}
    if (variable) {
        if (!value || defaultValue || prefix !== FONT_VAR_PREFIX || !/^[0-9]+$/.test(value)) {
            return undefined
        }

        return variable
    }

    return deserializeString(val)
}

const deserializeOverrides = (overridesFunc: CssValue): DecoratedStyleProperty | undefined => {
    const override = deserializeOverride(overridesFunc)
    if (!override) {
        return undefined
    }

    const styleProperty: DecoratedStyleProperty = {}

    const value = deserializeFontValue(override.value)
    if (!value) {
        return undefined
    }

    if (value !== DEFAULT_VALUE) {
        styleProperty.value = value
    }

    const fontOverrides = override.overrides.join(ARGS_DELIMITER).split(FONT_OVERRIDES_DELIMITER)
    if (fontOverrides.length !== FONT_OVERRIDES.length) {
        return undefined
    }

    styleProperty.overrides = FONT_OVERRIDES.reduce(
        (overridesObj: FontOverrides, overrideKey: string, index: number) => {
            const fontOverride = fontOverrides[index]
            if (fontOverride !== DEFAULT_VALUE) {
                ;(overridesObj as Record<string, any>)[overrideKey] = fontOverride
            }

            return overridesObj
        },
        {} as FontOverrides
    )

    return styleProperty
}

const serialize = (val: any): CssValue | undefined => {
    if (!_.isPlainObject(val) || _.isEmpty(val) || !hasValidKeys(val, VALID_KEYS)) {
        return undefined
    }

    const {value, overrides} = val

    const {fontValue, fontType} = serializeFontValue(value)
    if (!fontValue) {
        return undefined
    }

    if (overrides) {
        if (fontType === FONT_VALUE_TYPES.VALUE || !hasValidKeys(overrides, OVERRIDES_DATA_KEYS)) {
            return undefined
        }

        return serializeOverride({value: fontValue, overrides: [serializeOverrides(overrides)]})
    }

    return fontValue
}

const deserialize = (rule: CssValue): DecoratedStyleProperty | undefined => {
    if (rule.startsWith(OVERRIDE_FUNCTION_NAME)) {
        return deserializeOverrides(rule)
    }

    const value = deserializeFontValue(rule)
    if (value) {
        return {value}
    }
}

export default {
    serialize,
    deserialize
}
